import { action, observable, makeObservable } from 'mobx';
import { Model, IModelAttributes, attribute, entity } from 'Models/Model';
import * as Models from 'Models/Entities';

export interface IPassengerDetailEntityAttributes extends IModelAttributes {
	placeholder: string;

	passengerTypeId: string;
	passengerType: Models.PassengerTypeEntity | Models.IPassengerTypeEntityAttributes;
	passengers: Array<Models.PassengerDetailPassenger | Models.IPassengerDetailPassengerAttributes>;
	tickets: Array<Models.PassengerDetailTicket | Models.IPassengerDetailTicketAttributes>;
}
@entity('PassengerDetailEntity', 'Passenger Detail')
export default class PassengerDetailEntity extends Model implements IPassengerDetailEntityAttributes {
	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	/**
	 * Will add more attributes in the future.
	 */
	@observable
	@attribute<PassengerDetailEntity, string>()
	public placeholder: string;

	@observable
	@attribute()
	public passengerTypeId: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public passengerType: Models.PassengerTypeEntity;

	@observable
	@attribute({ isReference: true, manyReference: true })
	public passengers: Models.PassengerDetailPassenger[] = [];

	@observable
	@attribute({ isReference: true, manyReference: true })
	public tickets: Models.PassengerDetailTicket[] = [];

	constructor(attributes?: Partial<IPassengerDetailEntityAttributes>) {
		super(attributes);

		this.passengers = [];
		this.tickets = [];

		makeObservable(this);

		this.assignAttributes(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	public assignAttributes(attributes?: Partial<IPassengerDetailEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.placeholder !== undefined) {
				this.placeholder = attributes.placeholder;
			}
			if (attributes.passengerTypeId !== undefined) {
				this.passengerTypeId = attributes.passengerTypeId;
			}
			if (attributes.passengerType !== undefined) {
				if (attributes.passengerType === null) {
					this.passengerType = attributes.passengerType;
				} else if (attributes.passengerType instanceof Models.PassengerTypeEntity) {
					this.passengerType = attributes.passengerType;
					this.passengerTypeId = attributes.passengerType.id;
				} else {
					this.passengerType = new Models.PassengerTypeEntity(attributes.passengerType);
					this.passengerTypeId = this.passengerType.id;
				}
			}
			if (attributes.passengers !== undefined && Array.isArray(attributes.passengers)) {
				if (!this.passengers) {
					this.passengers = [];
				}
				for (const model of attributes.passengers) {
					if (model instanceof Models.PassengerDetailPassenger) {
						this.passengers.push(model);
					} else {
						this.passengers.push(new Models.PassengerDetailPassenger(model));
					}
				}
			}
			if (attributes.tickets !== undefined && Array.isArray(attributes.tickets)) {
				if (!this.tickets) {
					this.tickets = [];
				}
				for (const model of attributes.tickets) {
					if (model instanceof Models.PassengerDetailTicket) {
						this.tickets.push(model);
					} else {
						this.tickets.push(new Models.PassengerDetailTicket(model));
					}
				}
			}
		}
	}
}
